import React from "react";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

const formatTimeTick = (ts: number) => {
    try {
        return new Date(ts).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
    } catch {
        return "";
    }
};

const AreaChartBar = ({
    areaChartData,
    color,
    isShowDollarSign = true,
    fillOpacity = 1,
    strokeWidth = 1,
    timeDataKey,
    showDot = false,
    dotPayloadKey,
}: any) => {
    const useTimeAxis = Boolean(timeDataKey && areaChartData?.data?.length && areaChartData.data[0]?.[timeDataKey] != null);
    const renderDot = (item: { name: string; strokeColor: string }) => {
        if (!showDot) return false;
        if (dotPayloadKey) {
            return (props: any) => {
                const show = props.payload && props.payload[dotPayloadKey];
                if (!show) return <circle cx={props.cx} cy={props.cy} r={0} />;
                return (
                    <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={2.5}
                        fill={item.strokeColor}
                        stroke="#fff"
                        strokeWidth={1}
                    />
                );
            };
        }
        return { r: 2.5, fill: item.strokeColor, stroke: "#fff", strokeWidth: 1 };
    };
    return (
        <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={areaChartData.data} margin={{ left: -15, bottom: 28 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                <defs>
                    {areaChartData?.graphKeys?.map((item: any) => (
                        <linearGradient id={`color-${item.name}`} key={item.name} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={item.strokeColor} stopOpacity={fillOpacity * 0.6}/>
                            <stop offset="95%" stopColor={item.strokeColor} stopOpacity={0}/>
                        </linearGradient>
                    ))}
                </defs>
                {useTimeAxis ? (
                    <XAxis
                        type="number"
                        dataKey={timeDataKey}
                        domain={["dataMin", "dataMax"]}
                        tickLine={false}
                        tick={{ dx: 5, dy: 4 }}
                        padding={{ right: 35, left: 8 }}
                        fontSize={11}
                        tickFormatter={formatTimeTick}
                    />
                ) : (
                    <XAxis
                        dataKey="name"
                        tickLine={false}
                        tick={{ dx: 5, dy: 4 }}
                        padding={{ right: 35, left: 8 }}
                        fontSize={11}
                    />
                )}
                <YAxis
                    tickLine={false}
                    tickFormatter={(value) => (isShowDollarSign ? `$${value}` : value)}
                    padding={{top: 12}}
                    tick={{dy: -5}}
                    fontSize={10}
                />

                <Tooltip
                    labelFormatter={(label) =>
                        useTimeAxis && label != null ? formatTimeTick(Number(label)) : label
                    }
                    formatter={(value: number) => (isShowDollarSign ? `$${value}` : value)}
                />
                {areaChartData?.graphKeys?.map((item: any) => {
                    return (
                        <Area
                            key={item.name}
                            type="linear"
                            dataKey={item.name}
                            stroke={item.strokeColor}
                            strokeWidth={strokeWidth}
                            fillOpacity={fillOpacity}
                            fill={`url(#color-${item.name})`}
                            dot={renderDot(item)}
                            connectNulls
                        />
                    );
                })}
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default AreaChartBar;
